import { useState } from "react";
import axiosInstance from "../../api/axios";
import toast from "react-hot-toast";
import { PlusCircle, Trash2, Upload, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CreateProduct() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        title: "",
        description: "",
        title_en: "",
        description_en: "",
        shutters: [{ title: "", description: "" }],
        shutters_en: [{ title: "", description: "" }],
        images: [],
        videos: [],
        "3d_video": null,
        type: "product",
    });

    const [uploading, setUploading] = useState(false);
    const [creating, setCreating] = useState(false);
    const disabled = uploading || creating;

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleShutterChange = (index, field, value, lang = "fa") => {
        const updated = [...form[lang === "fa" ? "shutters" : "shutters_en"]];
        updated[index][field] = value;
        setForm((prev) => ({
            ...prev,
            [lang === "fa" ? "shutters" : "shutters_en"]: updated,
        }));
    };

    const addShutter = () => {
        setForm((prev) => ({
            ...prev,
            shutters: [...prev.shutters, { title: "", description: "" }],
            shutters_en: [...prev.shutters_en, { title: "", description: "" }],
        }));
    };

    const removeShutter = (index) => {
        if (form.shutters.length === 1) return;
        setForm((prev) => ({
            ...prev,
            shutters: prev.shutters.filter((_, i) => i !== index),
            shutters_en: prev.shutters_en.filter((_, i) => i !== index),
        }));
    };

    const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
    const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200 MB

    const handleFileUpload = async (e, type = "image", videoIndex = null) => {
        const file = e.target.files[0];
        if (!file) return;

        if (type === "image" && file.size > MAX_IMAGE_SIZE) {
            toast.error("حجم تصویر نباید بیشتر از ۵ مگابایت باشد");
            return;
        }
        if ((type === "video" || type === "3d_video") && file.size > MAX_VIDEO_SIZE) {
            toast.error("حجم ویدیو نباید بیشتر از ۲۰۰ مگابایت باشد");
            return;
        }
        if (type === "video_cover" && file.size > MAX_IMAGE_SIZE) {
            toast.error("حجم کاور نباید بیشتر از ۵ مگابایت باشد");
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const { data } = await axiosInstance.post("/api/file/upload-admin", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const uploaded = data.data;

            if (type === "3d_video") {
                setForm((prev) => ({
                    ...prev,
                    ["3d_video"]: {
                        video_file_id: uploaded.id,
                        path: uploaded.path,
                    },
                }));
            } else if (type === "video") {
                setForm((prev) => ({
                    ...prev,
                    videos: [
                        ...prev.videos,
                        {
                            video_file_id: uploaded.id,
                            video_path: uploaded.path,
                            cover_file_id: null,
                            cover_path: null,
                        },
                    ],
                }));
            } else if (type === "video_cover" && videoIndex !== null) {
                const updated = [...form.videos];
                updated[videoIndex].cover_file_id = uploaded.id;
                updated[videoIndex].cover_path = uploaded.path;
                setForm((prev) => ({ ...prev, videos: updated }));
            } else {
                setForm((prev) => ({
                    ...prev,
                    images: [...prev.images, { file_id: uploaded.id, path: uploaded.path }],
                }));
            }

            toast.success("فایل با موفقیت آپلود شد");
        } catch {
            toast.error("خطا در آپلود فایل");
        } finally {
            setUploading(false);
        }
    };

    const removeImage = (index) => {
        setForm((prev) => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index),
        }));
    };

    const removeVideo = (index) => {
        setForm((prev) => ({
            ...prev,
            videos: prev.videos.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !form.title.trim() ||
            !form.title_en.trim() ||
            !form.description.trim() ||
            !form.description_en.trim()
        ) {
            toast.error("تمام فیلدهای متنی الزامی هستند");
            return;
        }

        if (form.images.length < 3) {
            toast.error("حداقل باید ۳ تصویر آپلود شود");
            return;
        }

        if (
            form.shutters.some((s) => !s.title.trim() || !s.description.trim()) ||
            form.shutters_en.some((s) => !s.title.trim() || !s.description.trim())
        ) {
            toast.error("تمام ویژگی‌ها باید کامل شوند");
            return;
        }

        if (form.videos.some((v) => !v.cover_file_id)) {
            toast.error("هر ویدیو باید کاور داشته باشد");
            return;
        }

        setCreating(true);

        const cleanData = {
            ...form,
            images: form.images.map(({ file_id }) => ({ file_id })),
            videos: form.videos.map(({ video_file_id, cover_file_id }) => ({
                video_file_id,
                cover_file_id,
            })),
            "3d_video": form["3d_video"]
                ? {
                    video_file_id: form["3d_video"].video_file_id,
                    cover_file_id: form["3d_video"].video_file_id,
                }
                : undefined,
        };

        try {
            const { data } = await axiosInstance.post("/api/product/create", cleanData);
            if (data.success) {
                toast.success(data.message || "محصول با موفقیت ایجاد شد");
                setForm({
                    title: "",
                    description: "",
                    title_en: "",
                    description_en: "",
                    shutters: [{ title: "", description: "" }],
                    shutters_en: [{ title: "", description: "" }],
                    images: [],
                    videos: [],
                    "3d_video": null,
                    type: "product",
                });
                navigate('/products');
            }
        } catch {
            toast.error("خطا در ایجاد محصول");
        } finally {
            setCreating(false);
        }
    };

    return (
        <section
            className={`w-full max-w-4xl mx-auto transition ${disabled ? "opacity-50 pointer-events-none" : ""
                }`}
        >
            <h2 className="text-xl font-semibold text-background mb-6 py-3">
                ایجاد محصول جدید
            </h2>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div>
                    <label className="text-sm font-medium text-background">نوع محصول</label>
                    <select
                        value={form.type}
                        onChange={(e) => handleChange("type", e.target.value)}
                        className="w-full cursor-pointer mt-1 px-4 py-2 rounded-lg bg-white/50 text-background border border-black/5 focus:border-accent outline-none"
                    >
                        <option value="product">ماشین‌آلات</option>
                        <option value="service">خدمات</option>
                    </select>
                </div>

                <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-medium text-background">عنوان فارسی</label>
                        <input
                            type="text"
                            value={form.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                            className="w-full px-4 py-2 mt-1 rounded-lg bg-white/50 text-background border border-black/5 focus:border-accent outline-none"
                            placeholder="عنوان فارسی محصول"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-background">عنوان انگلیسی</label>
                        <input
                            type="text"
                            value={form.title_en}
                            onChange={(e) => handleChange("title_en", e.target.value)}
                            className="w-full px-4 py-2 mt-1 rounded-lg bg-white/50 text-background border border-black/5 focus:border-accent outline-none"
                            placeholder="English Product Title"
                            dir="ltr"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-background">توضیحات فارسی</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            className="w-full px-4 py-2 mt-1 rounded-lg bg-white/50 text-background border border-black/5 focus:border-accent outline-none min-h-[100px]"
                            placeholder="توضیحات فارسی محصول"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-background">توضیحات انگلیسی</label>
                        <textarea
                            value={form.description_en}
                            onChange={(e) => handleChange("description_en", e.target.value)}
                            className="w-full px-4 py-2 mt-1 rounded-lg bg-white/50 text-background border border-black/5 focus:border-accent outline-none min-h-[100px]"
                            placeholder="English Product Description"
                            dir="ltr"
                        />
                    </div>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold text-background">ویژگی‌ها</h3>
                        <button
                            type="button"
                            onClick={addShutter}
                            className="bg-accent px-3 py-1.5 rounded-md flex items-center gap-1 font-medium text-sm text-foreground"
                        >
                            <PlusCircle size={16} /> افزودن ویژگی
                        </button>
                    </div>

                    <div className="flex flex-col gap-4">
                        {form.shutters.map((shutter, index) => (
                            <div
                                key={index}
                                className="grid grid-cols-1 tablet:grid-cols-2 gap-x-3 gap-y-2 bg-white/50 border border-white/10 rounded-lg p-3 pt-6 pb-1 relative"
                            >
                                {form.shutters.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeShutter(index)}
                                        className="absolute top-3 left-3 bg-accent text-white rounded-md p-2"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                                <div>
                                    <label className="text-xs font-medium text-background/80">
                                        عنوان فارسی
                                    </label>
                                    <input
                                        type="text"
                                        value={shutter.title}
                                        onChange={(e) =>
                                            handleShutterChange(index, "title", e.target.value, "fa")
                                        }
                                        className="w-full px-3 py-1.5 mt-1 rounded-md bg-white/50 text-background border border-black/5 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-background/80">
                                        عنوان انگلیسی
                                    </label>
                                    <input
                                        type="text"
                                        value={form.shutters_en[index]?.title || ""}
                                        onChange={(e) =>
                                            handleShutterChange(index, "title", e.target.value, "en")
                                        }
                                        className="w-full px-3 py-1.5 mt-1 rounded-md bg-white/50 text-background border border-black/5 outline-none"
                                        dir="ltr"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-background/80">
                                        توضیح فارسی
                                    </label>
                                    <textarea
                                        value={shutter.description}
                                        onChange={(e) =>
                                            handleShutterChange(index, "description", e.target.value, "fa")
                                        }
                                        className="w-full px-3 py-1.5 mt-1 rounded-md bg-white/50 text-background border border-black/5 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-background/80">
                                        توضیح انگلیسی
                                    </label>
                                    <textarea
                                        value={form.shutters_en[index]?.description || ""}
                                        onChange={(e) =>
                                            handleShutterChange(index, "description", e.target.value, "en")
                                        }
                                        className="w-full px-3 py-1.5 mt-1 rounded-md bg-white/50 text-background border border-black/5 outline-none"
                                        dir="ltr"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-semibold text-background mb-3">
                        تصاویر محصول
                    </h3>
                    <div className="bg-white/50 rounded-xl p-3 flex flex-wrap items-center gap-3">
                        {form.images.map((img, i) => (
                            <div key={i} className="relative">
                                <img
                                    src={`${process.env.REACT_APP_BASE_URL}/${img.path}`}
                                    alt="product"
                                    className="size-24 object-cover rounded-lg border border-white/50"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeImage(i)}
                                    className="absolute top-1.5 left-1.5 bg-accent text-foreground rounded-md p-1.5"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                        <label className="w-24 h-24 border-2 text-black/50 border-dashed border-black/20 rounded-lg flex items-center justify-center cursor-pointer hover:border-accent hover:bg-orange-100 hover:text-accent transition">
                            {uploading ? (
                                <Loader2 className="animate-spin" size={22} />
                            ) : (
                                <Upload size={22} />
                            )}
                            <input
                                type="file"
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, "image")}
                                accept="image/*"
                                disabled={disabled}
                            />
                        </label>
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-semibold text-background mb-3">
                        ویدیوها (اختیاری)
                    </h3>
                    <div className="bg-white/50 rounded-xl p-3 flex flex-col gap-3">
                        {form.videos.map((vid, i) => (
                            <div
                                key={i}
                                className="flex flex-col sm:flex-row items-center justify-between bg-white/70 rounded-lg p-2 px-4"
                            >
                                <span className="text-sm text-background">
                                    🎥 {vid.video_path.split("/").pop()}
                                </span>
                                {vid.cover_path ? (
                                    <img
                                        src={`${process.env.REACT_APP_BASE_URL}/${vid.cover_path}`}
                                        alt="cover"
                                        className="size-16 rounded-md object-cover border border-black/10"
                                    />
                                ) : (
                                    <label className="text-black/50 border-2 border-dashed border-black/20 size-16 rounded-md flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-accent hover:bg-orange-100 hover:text-accent">
                                        <Upload size={16} />
                                        <span className="text-xs">کاور</span>
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => handleFileUpload(e, "video_cover", i)}
                                            disabled={disabled}
                                        />
                                    </label>
                                )}
                                <button
                                    type="button"
                                    onClick={() => removeVideo(i)}
                                    className="bg-accent p-2 rounded-md text-foreground"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}

                        <label className="w-full text-black/50 border-2 border-dashed border-black/20 rounded-lg flex items-center justify-center cursor-pointer hover:border-accent hover:bg-orange-100 hover:text-accent py-12 transition">
                            {uploading ? (
                                <Loader2 className="animate-spin" size={18} />
                            ) : (
                                <Upload size={18} />
                            )}
                            <input
                                type="file"
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, "video")}
                                accept="video/*"
                                disabled={disabled}
                            />
                        </label>
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-semibold text-background mb-3">
                        ویدیوی اسکرولی (اختیاری)
                    </h3>
                    <div className="bg-white/50 rounded-xl p-3">
                        {form["3d_video"] ? (
                            <div className="flex items-center justify-between text-background text-sm">
                                🎬 {form["3d_video"].path.split("/").pop()}
                                <button
                                    type="button"
                                    onClick={() => setForm((prev) => ({ ...prev, ["3d_video"]: null }))}
                                    className="text-red-400 hover:text-red-600"
                                >
                                    حذف
                                </button>
                            </div>
                        ) : (
                            <label className="px-4 py-12 text-black/50 border-2 border-dashed border-black/20 rounded-lg flex items-center justify-center cursor-pointer hover:border-accent hover:bg-orange-100 hover:text-accent transition">
                                {uploading ? (
                                    <Loader2 className="animate-spin" size={18} />
                                ) : (
                                    <Upload size={18} />
                                )}
                                <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => handleFileUpload(e, "3d_video")}
                                    accept="video/*"
                                    disabled={disabled}
                                />
                            </label>
                        )}
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={creating}
                    className="bg-accent text-foreground px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-accent transition-colors"
                >
                    {creating && <Loader2 className="animate-spin" size={18} />}
                    {creating ? "در حال ایجاد..." : "ایجاد محصول"}
                </button>
            </form>
        </section>
    );
}